import React from 'react';
import { Star, Quote, BadgeCheck, TrendingUp } from 'lucide-react';

import avatarArjun from '@/assets/avatar-arjun.png';
import avatarPriya from '@/assets/avatar-priya.png';
import avatarRahul from '@/assets/avatar-rahul.png';
import avatarSneha from '@/assets/avatar-sneha.png';
import avatarVikash from '@/assets/avatar-vikash.png';
import avatarAnanya from '@/assets/avatar-ananya.png';

const reviews = [
  {
    name: 'Arjun Sharma',
    rating: 5,
    review: 'These notes are a lifesaver! The handwriting is neat and all important topics are covered. Scored 76 in Science!',
    avatar: avatarArjun,
    marks: '76/80',
    verified: true,
  },
  {
    name: 'Priya Patel',
    rating: 5,
    review: 'Best investment for board exams. The diagrams and formulas are so well organized. Highly recommend!',
    avatar: avatarPriya,
    marks: '74/80',
    verified: true,
  },
  {
    name: 'Rahul Kumar',
    rating: 5,
    review: 'Revised the entire syllabus in just 2 days before exams. These notes made it possible. Thank you!',
    avatar: avatarRahul,
    marks: '71/80',
    verified: true,
  },
  {
    name: 'Sneha Gupta',
    rating: 5,
    review: 'Very helpful notes with clear explanations. The summary sections are perfect for last-minute revision.',
    avatar: avatarSneha,
    marks: '75/80',
    verified: true,
  },
  {
    name: 'Vikash Singh',
    rating: 5,
    review: 'My son improved from 56 to 74 marks after using these notes. Worth every rupee spent! As a parent, I highly recommend.',
    avatar: avatarVikash,
    marks: '56→74',
    verified: true,
  },
  {
    name: 'Ananya Reddy',
    rating: 5,
    review: 'The chapter-wise breakdown is amazing. Each concept is explained with examples and diagrams. Best notes ever!',
    avatar: avatarAnanya,
    marks: '78/80',
    verified: true,
  },
];

const stats = [
  { value: '1,300+', label: 'Happy Students' },
  { value: '4.8', label: 'Average Rating' },
  { value: '90%+', label: 'Score Above 70' },
];

const ReviewsSection: React.FC = () => {
  const averageRating = 4.8;

  return (
    <section 
      id="reviews" 
      aria-labelledby="reviews-heading"
      className="py-12 md:py-16 bg-muted/30"
    >
      <div className="container px-4 md:px-6">
        <div className="mx-auto max-w-5xl">
          {/* Overall Rating Header */}
          <header className="mb-10 text-center">
            <h2 
              id="reviews-heading"
              className="mb-4 text-2xl font-bold tracking-tight text-foreground md:text-3xl"
            >
              What Our Students Say
            </h2>
            <div className="flex items-center justify-center gap-3" aria-label={`Average rating: ${averageRating} out of 5 stars`}>
              <div className="flex" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star}
                    className={`h-7 w-7 ${star <= Math.floor(averageRating) ? 'fill-gold text-gold' : 'fill-gold/30 text-gold/30'}`}
                  />
                ))}
              </div>
              <span className="text-3xl font-bold text-foreground">{averageRating}</span>
              <span className="text-lg text-muted-foreground">/ 5</span>
            </div>
            <p className="mt-2 text-muted-foreground">Based on 1,300+ verified student reviews</p>
          </header>

          {/* Stats Bar */}
          <div className="mb-10 grid grid-cols-3 gap-4 rounded-2xl border border-border/50 bg-card p-4 md:p-6 shadow-card">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-2xl font-bold text-primary md:text-3xl">{stat.value}</p>
                <p className="text-xs text-muted-foreground md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Reviews Grid */}
          <div 
            className="grid gap-5 md:grid-cols-2 lg:grid-cols-3"
            role="list"
            aria-label="Student testimonials for Class 10 Science notes"
          >
            {reviews.map((review, index) => (
              <article 
                key={index}
                role="listitem"
                className="group relative rounded-xl border border-border/50 bg-card p-5 shadow-card transition-all duration-300 hover:border-primary/30 hover:shadow-lg"
              >
                {/* Quote Icon */}
                <Quote className="absolute right-4 top-4 h-8 w-8 text-primary/10" />
                
                {/* Header with Avatar */}
                <div className="mb-4 flex items-center gap-3">
                  <img 
                    src={review.avatar}
                    alt={`${review.name} avatar`}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-primary/20"
                    width={48}
                    height={48}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-foreground">{review.name}</p>
                      {review.verified && (
                        <BadgeCheck className="h-4 w-4 text-primary" aria-label="Verified student" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Rating Stars */}
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex" role="img" aria-label={`${review.rating} out of 5 stars`}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star 
                        key={star}
                        aria-hidden="true"
                        className={`h-4 w-4 ${star <= review.rating ? 'fill-gold text-gold' : 'fill-muted text-muted'}`}
                      />
                    ))}
                  </div>
                  {/* Score Badge */}
                  <div className="ml-auto flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-semibold text-success-dark">
                    <TrendingUp className="h-3 w-3" />
                    {review.marks}
                  </div>
                </div>

                {/* Review Text */}
                <blockquote className="text-sm leading-relaxed text-muted-foreground">
                  "{review.review}"
                </blockquote>
              </article>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-center text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-primary" />
              <span>All reviews verified</span>
            </div>
            <span className="hidden md:inline">•</span>
            <div className="flex items-center gap-2">
              <Star className="h-5 w-5 fill-gold text-gold" />
              <span>Trusted by 1,300+ students across India</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
